import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { socketService } from '../services/socketService';
import { useGameStore } from '../store/gameStore';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: radial-gradient(circle at center, #2a8d2a 0%, #1a5d1a 50%, #0f3d0f 100%);
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 40px;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;

const RoleButton = styled.button<{ $isLeader: boolean }>`
  padding: 15px 30px;
  font-size: 1.2rem;
  background: ${props => props.$isLeader 
    ? 'linear-gradient(135deg, #27ae60, #219a52)'
    : 'linear-gradient(135deg, #3498db, #2980b9)'};
  color: white;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 15px ${props => props.$isLeader 
    ? 'rgba(39, 174, 96, 0.3)'
    : 'rgba(52, 152, 219, 0.3)'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => props.$isLeader 
      ? 'rgba(39, 174, 96, 0.4)'
      : 'rgba(52, 152, 219, 0.4)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 480px;
  animation: slideIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  transform: translateZ(0);
  
  @keyframes slideIn {
    from {
      transform: translateX(100%) translateY(-20px) translateZ(0);
      opacity: 0;
    }
    to {
      transform: translateX(0) translateY(0) translateZ(0);
      opacity: 1;
    }
  }
`;

const ErrorNotification = styled.div`
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: #ffffff;
  padding: 24px 28px;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(255, 71, 87, 0.3), 0 5px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.3rem;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  align-items: center;
  gap: 18px;
  backdrop-filter: blur(15px);
  overflow: hidden;
  
  &::before {
    content: "⚠️";
    font-size: 2rem;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
    animation: pulse 2s infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #ff6b6b, #ff8e8e, #ff6b6b);
    animation: shimmer 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.15) rotate(90deg);
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.9) rotate(90deg);
  }
`;

const DebugInfo = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9em;
  margin-top: 20px;
  text-align: center;
`;

const CrownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L2 8.5L5 20H19L22 8.5L12 2ZM12 5.311L17.949 9.164L16.172 16H7.828L6.051 9.164L12 5.311Z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
  </svg>
);

const InitialScreen: React.FC = () => {
  const navigate = useNavigate();
  const { setGameRole, setGamePhase } = useGameStore();
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [leaderExists, setLeaderExists] = useState<boolean>(false);
  const [isCheckingLeader, setIsCheckingLeader] = useState<boolean>(true);

  const handleCloseError = () => {
    setError('');
  };

  // Check if leader exists when component mounts
  React.useEffect(() => {
    const checkLeaderStatus = async () => {
      try {
        const response = await socketService.checkGameAvailability();
        setLeaderExists(!response.canBecomeLeader);
      } catch (err) {
        console.error('Error checking leader status:', err);
      } finally {
        setIsCheckingLeader(false);
      }
    };

    checkLeaderStatus();
  }, []);

  const handleRoleSelect = async (role: 'leader' | 'member') => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setError('');
      console.log(`Attempting to become ${role}...`);

      if (role === 'leader') {
        console.log('Calling checkGameAvailability...');
        const response = await socketService.checkGameAvailability();
        console.log('Leader availability response:', response);

        if (response.error) {
          console.log('Error from server:', response.error);
          setError(response.error);
          return;
        }

        if (response.canBecomeLeader) {
          console.log('Can become leader, setting role and navigating...');
          setGameRole('leader');
          setGamePhase('setup');
          navigate('/leader-setup');
        } else {
          console.log('Cannot become leader');
          setError('A game is already in progress');
        }
      } else {
        console.log('Calling checkGameStatus...');
        const response = await socketService.checkGameStatus();
        console.log('Member check response:', response);

        if (response.error) {
          console.log('Error from server:', response.error);
          setError(response.error);
          return;
        }

        if (response.phase === 'setup') {
          setError('Leader is still setting up the game. Please wait.');
        } else if (response.phase === 'playing') {
          setError('A game is in progress. No entry allowed.');
        } else if (response.phase === 'waiting' && response.setupComplete) {
          console.log('Game ready for members, setting role and navigating...');
          setGameRole('member');
          setGamePhase('waiting');
          navigate('/waiting-room');
        } else {
          setError('No game available. Please wait for a leader to create one.');
        }
      }
    } catch (err) {
      console.error('Error in role selection:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to server. Please make sure the server is running.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {error && (
        <NotificationContainer>
          <ErrorNotification>
            {error}
            <CloseButton onClick={handleCloseError}>×</CloseButton>
          </ErrorNotification>
        </NotificationContainer>
      )}
      <Container>
        <Card>
          <Title>Welcome to Bagels Knock card game</Title>
                     <ButtonContainer>
             <RoleButton
               onClick={() => handleRoleSelect('leader')}
               disabled={isProcessing || leaderExists || isCheckingLeader}
               $isLeader={true}
             >
               <CrownIcon />
               {isCheckingLeader 
                 ? 'Checking...' 
                 : leaderExists 
                   ? 'Leader loading game' 
                   : isProcessing 
                     ? 'Processing...' 
                     : 'I am the Leader'
               }
             </RoleButton>
             <RoleButton
               onClick={() => handleRoleSelect('member')}
               disabled={isProcessing}
               $isLeader={false}
             >
               <UserIcon />
               {isProcessing ? 'Processing...' : 'I am a Member'}
             </RoleButton>
           </ButtonContainer>
          <DebugInfo>
            Server should be running on: https://knock-server-yv8c.onrender.com
          </DebugInfo>
        </Card>
      </Container>
    </>
  );
};

export default InitialScreen; 